import { format } from "date-fns";
import { Pencil, Loader2, Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Roles } from "@/enums/roles.enum";
import { IAHJ } from "@/types/ahj";

import { GetProjectResponseDto } from "@/dto/get-project-response.dto";
import { GetProjectsResponseDto } from "@/dto/get-projects-response.dto";
import { GetUserResponseDto } from "@/dto/get-user-response.dto";
import { UserDTO } from "@/services/authService";
import { GetUsersResponse } from "@/services/userService";
import { GetAHJsResponse } from "@/services/ahjService";

interface MobileProjectInfoProps {
  project: GetProjectsResponseDto;
  projectDetails: GetProjectResponseDto;
  user: UserDTO | null;
  editingField: string | null;
  setEditingField: (
    field:
      | "permitNumber"
      | "estimatedReviewDateByAHJ"
      | "projectManager"
      | "ahj"
      | null
  ) => void;
  tempPermitNumber: string;
  setTempPermitNumber: (value: string) => void;
  tempEstimatedReviewDate: string;
  setTempEstimatedReviewDate: (value: string) => void;
  tempProjectManager: string;
  setTempProjectManager: (value: string) => void;
  tempAHJ: string;
  setTempAHJ: (value: string) => void;
  updatingField: string | null;
  handleSaveField: () => void;
  canPerformActions: boolean | undefined;
  usersData: GetUsersResponse | undefined;
  ahjsData: GetAHJsResponse | undefined;
}

export const MobileProjectInfo = ({
  project,
  projectDetails,
  user,
  editingField,
  setEditingField,
  tempPermitNumber,
  setTempPermitNumber,
  tempEstimatedReviewDate,
  setTempEstimatedReviewDate,
  tempProjectManager,
  setTempProjectManager,
  tempAHJ,
  setTempAHJ,
  updatingField,
  handleSaveField,
  canPerformActions,
  usersData,
  ahjsData,
}: MobileProjectInfoProps) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 gap-4">
      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">Created By</span>
        <span className="text-sm font-medium">
          {`${project.createdBy.firstName} ${project.createdBy.lastName}`.trim()}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">Created Date</span>
        <span className="text-sm font-medium">
          {format(project.createdAt, "MMM d, yyyy")}
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Permit #</span>
          {canPerformActions &&
            user?.role === Roles.ADMIN &&
            editingField !== "permitNumber" && (
              <Pencil
                className="h-3.5 w-3.5 cursor-pointer"
                onClick={() => setEditingField("permitNumber")}
              />
            )}
        </div>
        {editingField === "permitNumber" ? (
          <div className="flex items-center gap-2">
            <Input
              value={tempPermitNumber}
              onChange={(e) => setTempPermitNumber(e.target.value)}
              placeholder="Enter permit #"
              className="h-8 text-sm"
              autoFocus
              disabled={updatingField === "permitNumber"}
            />
            <Button
              size="sm"
              disabled={updatingField === "permitNumber"}
              onClick={handleSaveField}
              className="h-8 px-2"
            >
              {updatingField === "permitNumber" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingField(null)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <span className="text-sm font-medium">
            {projectDetails?.permitNumber || (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
        )}
      </div>

      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">Address</span>
        <span className="text-sm font-medium text-right">
          {project.address}, {project.city}, {project.state} {project.zip}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">Service</span>
        <span className="text-sm font-medium">{project.service}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">Building Owner</span>
        <span className="text-sm font-medium">{project.buildingOwner}</span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            Estimated Review Date by AHJ
          </span>
          {canPerformActions &&
            user?.role === Roles.ADMIN &&
            editingField !== "estimatedReviewDateByAHJ" && (
              <Pencil
                className="h-3.5 w-3.5 cursor-pointer"
                onClick={() => setEditingField("estimatedReviewDateByAHJ")}
              />
            )}
        </div>
        {editingField === "estimatedReviewDateByAHJ" ? (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={tempEstimatedReviewDate}
              onChange={(e) => setTempEstimatedReviewDate(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              disabled={updatingField === "estimatedReviewDateByAHJ"}
            />
            <Button
              size="sm"
              disabled={updatingField === "estimatedReviewDateByAHJ"}
              onClick={handleSaveField}
              className="h-8 px-2"
            >
              {updatingField === "estimatedReviewDateByAHJ" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingField(null)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <span className="text-sm font-medium">
            {projectDetails?.estimatedReviewDateByAHJ ? (
              format(
                new Date(projectDetails.estimatedReviewDateByAHJ),
                "MMM d, yyyy"
              )
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            QWS Project Manager
          </span>
          {canPerformActions &&
            user?.role === Roles.ADMIN &&
            editingField !== "projectManager" && (
              <Pencil
                className="h-3.5 w-3.5 cursor-pointer"
                onClick={() => setEditingField("projectManager")}
              />
            )}
        </div>
        {editingField === "projectManager" ? (
          <div className="flex items-center gap-2">
            <Select
              value={tempProjectManager || "none"}
              onValueChange={(v) =>
                setTempProjectManager(v === "none" ? "" : v)
              }
              disabled={updatingField === "projectManager"}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select Admin user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {usersData?.users
                  .filter((u: GetUserResponseDto) => u.role === Roles.ADMIN)
                  .map((u: GetUserResponseDto) => (
                    <SelectItem key={u._id} value={u._id}>
                      {`${u.firstName} ${u.lastName}`.trim() || u.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={updatingField === "projectManager"}
              onClick={handleSaveField}
              className="h-8 px-2"
            >
              {updatingField === "projectManager" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingField(null)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <span className="text-sm font-medium">
            {projectDetails?.projectManager ? (
              `${projectDetails.projectManager.firstName} ${projectDetails.projectManager.lastName}`.trim()
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">AHJ</span>
          {canPerformActions &&
            user?.role === Roles.ADMIN &&
            editingField !== "ahj" && (
              <Pencil
                className="h-3.5 w-3.5 cursor-pointer"
                onClick={() => setEditingField("ahj")}
              />
            )}
        </div>
        {editingField === "ahj" ? (
          <div className="flex items-center gap-2">
            <Select
              value={tempAHJ || "none"}
              onValueChange={(v) => setTempAHJ(v === "none" ? "" : v)}
              disabled={updatingField === "ahj"}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select AHJ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {ahjsData?.ahjs
                  .sort((a: IAHJ, b: IAHJ) => {
                    if (a.type !== b.type) return a.type.localeCompare(b.type);
                    if (a.state !== b.state)
                      return a.state.localeCompare(b.state);
                    return a.name.localeCompare(b.name);
                  })
                  .map((ahj: IAHJ) => (
                    <SelectItem key={ahj._id} value={ahj._id}>
                      {ahj.name} ({ahj.state}) - {ahj.type}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={updatingField === "ahj"}
              onClick={handleSaveField}
              className="h-8 px-2"
            >
              {updatingField === "ahj" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingField(null)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {projectDetails?.ahj ? (
                `${projectDetails.ahj.name} (${projectDetails.ahj.state})`
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </span>
            {projectDetails?.ahj && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  const viewDetailsBtn = document.getElementById(
                    "mobile-ahj-view-details"
                  );
                  if (viewDetailsBtn) viewDetailsBtn.click();
                }}
              >
                <Info className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);
